/**
 * Notification Permission Banner Component
 * Displays a banner to request notification permissions from the user
 */

import {
  requestPermission,
  subscribeToBrowserNotifications,
  shouldPromptForPermission,
  type BrowserNotificationsState,
} from '../services/browser-notifications';

/**
 * Banner options
 */
export interface NotificationPermissionBannerOptions {
  /** Parent element to append the banner to */
  parent: HTMLElement;
  /** Optional CSS class for the banner container */
  className?: string;
  /** Position: 'top' or 'bottom' */
  position?: 'top' | 'bottom';
  /** Custom title text */
  title?: string;
  /** Custom description text */
  description?: string;
  /** Custom enable button text */
  enableButtonText?: string;
  /** Custom dismiss button text */
  dismissButtonText?: string;
  /** Callback when notifications are enabled */
  onEnabled?: () => void;
  /** Callback when the banner is dismissed */
  onDismissed?: () => void;
  /** Whether to show the banner even after dismissal (until permission is granted or denied) */
  persistent?: boolean;
}

/**
 * Storage key for dismissed state
 */
const DISMISSED_KEY = 'wyden-notification-banner-dismissed';

/**
 * Check if banner was dismissed
 */
function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Mark banner as dismissed
 */
function setDismissed(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear dismissed state
 */
export function clearDismissedState(): void {
  try {
    localStorage.removeItem(DISMISSED_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Create the notification permission banner
 */
export function createNotificationPermissionBanner(
  options: NotificationPermissionBannerOptions
): {
  element: HTMLElement;
  show: () => void;
  hide: () => void;
  destroy: () => void;
} {
  const {
    parent,
    className = '',
    position = 'top',
    title = 'Enable Notifications',
    description = 'Get notified about pending approvals, request updates, and comments.',
    enableButtonText = 'Enable',
    dismissButtonText = 'Not Now',
    onEnabled,
    onDismissed,
    persistent = false,
  } = options;

  // Create banner element
  const banner = document.createElement('div');
  banner.className = `notification-permission-banner ${className}`.trim();
  banner.setAttribute('role', 'alert');
  banner.setAttribute('aria-live', 'polite');

  // Set styles
  banner.style.cssText = `
    display: none;
    position: fixed;
    ${position}: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    padding: 1rem;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%);
    backdrop-filter: blur(8px);
    border-${position === 'top' ? 'bottom' : 'top'}: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 ${position === 'top' ? '4px' : '-4px'} 6px -1px rgba(0, 0, 0, 0.1),
                0 ${position === 'top' ? '2px' : '-2px'} 4px -1px rgba(0, 0, 0, 0.06);
    animation: slideIn${position === 'top' ? 'Down' : 'Up'} 0.3s ease-out;
  `;

  // Create inner content container
  const content = document.createElement('div');
  content.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    gap: 1rem;
    flex-wrap: wrap;
  `;

  // Create text container
  const textContainer = document.createElement('div');
  textContainer.style.cssText = `
    flex: 1;
    min-width: 200px;
  `;

  // Create title
  const titleElement = document.createElement('h3');
  titleElement.textContent = title;
  titleElement.style.cssText = `
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    font-weight: 600;
    color: white;
  `;

  // Create description
  const descriptionElement = document.createElement('p');
  descriptionElement.textContent = description;
  descriptionElement.style.cssText = `
    margin: 0;
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.9);
  `;

  textContainer.appendChild(titleElement);
  textContainer.appendChild(descriptionElement);

  // Create buttons container
  const buttonsContainer = document.createElement('div');
  buttonsContainer.style.cssText = `
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
  `;

  // Create enable button
  const enableButton = document.createElement('button');
  enableButton.textContent = enableButtonText;
  enableButton.type = 'button';
  enableButton.style.cssText = `
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6366f1;
    background: white;
    border: none;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.15s ease;
  `;
  enableButton.addEventListener('mouseenter', () => {
    enableButton.style.background = '#f1f5f9';
  });
  enableButton.addEventListener('mouseleave', () => {
    enableButton.style.background = 'white';
  });

  // Create dismiss button
  const dismissButton = document.createElement('button');
  dismissButton.textContent = dismissButtonText;
  dismissButton.type = 'button';
  dismissButton.style.cssText = `
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: white;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.15s ease;
  `;
  dismissButton.addEventListener('mouseenter', () => {
    dismissButton.style.background = 'rgba(255, 255, 255, 0.2)';
  });
  dismissButton.addEventListener('mouseleave', () => {
    dismissButton.style.background = 'rgba(255, 255, 255, 0.1)';
  });

  buttonsContainer.appendChild(enableButton);
  buttonsContainer.appendChild(dismissButton);

  content.appendChild(textContainer);
  content.appendChild(buttonsContainer);
  banner.appendChild(content);

  // Add animation keyframes
  if (!document.getElementById('notification-banner-styles')) {
    const style = document.createElement('style');
    style.id = 'notification-banner-styles';
    style.textContent = `
      @keyframes slideInDown {
        from {
          transform: translateY(-100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      @keyframes slideInUp {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      @keyframes slideOutUp {
        from {
          transform: translateY(0);
          opacity: 1;
        }
        to {
          transform: translateY(-100%);
          opacity: 0;
        }
      }
      @keyframes slideOutDown {
        from {
          transform: translateY(0);
          opacity: 1;
        }
        to {
          transform: translateY(100%);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // State
  let isVisible = false;
  let unsubscribe: (() => void) | null = null;

  /**
   * Show the banner
   */
  const show = (): void => {
    if (isVisible) return;

    // Check if we should show
    if (!shouldPromptForPermission()) return;
    if (!persistent && wasDismissed()) return;

    banner.style.display = 'block';
    banner.style.animation = `slideIn${position === 'top' ? 'Down' : 'Up'} 0.3s ease-out`;
    isVisible = true;
  };

  /**
   * Hide the banner with animation
   */
  const hide = (): void => {
    if (!isVisible) return;

    banner.style.animation = `slideOut${position === 'top' ? 'Up' : 'Down'} 0.3s ease-out forwards`;

    setTimeout(() => {
      banner.style.display = 'none';
      isVisible = false;
    }, 300);
  };

  /**
   * Destroy the banner and clean up
   */
  const destroy = (): void => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    banner.remove();
  };

  // Handle enable click
  enableButton.addEventListener('click', async () => {
    enableButton.disabled = true;
    enableButton.textContent = 'Requesting...';

    const permission = await requestPermission();

    if (permission === 'granted') {
      hide();
      onEnabled?.();
    } else if (permission === 'denied') {
      // Update UI to show denied state
      descriptionElement.textContent =
        'Notifications are blocked. You can enable them in your browser settings.';
      enableButton.style.display = 'none';
      dismissButton.textContent = 'Close';
    } else {
      enableButton.disabled = false;
      enableButton.textContent = enableButtonText;
    }
  });

  // Handle dismiss click
  dismissButton.addEventListener('click', () => {
    if (!persistent) {
      setDismissed();
    }
    hide();
    onDismissed?.();
  });

  // Subscribe to state changes
  unsubscribe = subscribeToBrowserNotifications((state: BrowserNotificationsState) => {
    if (state.permission === 'granted' || state.permission === 'denied') {
      hide();
    }
  });

  // Append to parent
  parent.appendChild(banner);

  // Auto-show if appropriate
  if (shouldPromptForPermission() && (persistent || !wasDismissed())) {
    // Delay show slightly for better UX
    setTimeout(show, 1000);
  }

  return {
    element: banner,
    show,
    hide,
    destroy,
  };
}

/**
 * NotificationPermissionBanner class for object-oriented usage
 */
export class NotificationPermissionBanner {
  private instance: ReturnType<typeof createNotificationPermissionBanner>;

  constructor(options: NotificationPermissionBannerOptions) {
    this.instance = createNotificationPermissionBanner(options);
  }

  getElement(): HTMLElement {
    return this.instance.element;
  }

  show(): void {
    this.instance.show();
  }

  hide(): void {
    this.instance.hide();
  }

  destroy(): void {
    this.instance.destroy();
  }
}
